/**
 * UCC Analytics Hooks
 *
 * React Query hooks for UCC filing analytics dashboards.
 *
 * Query parameter names follow schema-registry/ucc-filing-analytics.openapi.yaml
 * (`jurisdiction`, `filingType`, `dateFrom`, `dateTo`, `limit`, `offset`). The
 * hooks previously sent `jurisdictionCode`, `from` and `to`, which the spec
 * does not declare and the ucc-filing analytics route ignores, so the
 * Analytics page's jurisdiction filter changed nothing it read.
 */

import { useQuery } from '@tanstack/react-query';
import type { operations } from '@aaimplatform/institutional-types/ucc-filing-analytics';
import { uccFilingAnalyticsClient } from '../api/typed-clients';
import { unwrapUccResponse } from './ucc-response';

export function useFilingVolume(params: operations['getFilingVolumeSummary']['parameters']['query']) {
  return useQuery({
    queryKey: ['uccFilingVolume', params],
    queryFn: async () => {
      const result = await uccFilingAnalyticsClient.GET('/ucc-analytics/filing-volume', {
        params: { query: params },
      });
      return unwrapUccResponse(result);
    },
    enabled: !!params.tenantId,
  });
}

export function useTurnaroundTime(params: operations['getTurnaroundTime']['parameters']['query']) {
  return useQuery({
    queryKey: ['uccTurnaroundTime', params],
    queryFn: async () => {
      const result = await uccFilingAnalyticsClient.GET('/ucc-analytics/turnaround-time', {
        params: { query: params },
      });
      return unwrapUccResponse(result);
    },
    enabled: !!params.tenantId,
  });
}

export function useRejectionRates(params: operations['getRejectionRates']['parameters']['query']) {
  return useQuery({
    queryKey: ['uccRejectionRates', params],
    queryFn: async () => {
      const result = await uccFilingAnalyticsClient.GET('/ucc-analytics/rejection-rates', {
        params: { query: params },
      });
      return unwrapUccResponse(result);
    },
    enabled: !!params.tenantId,
  });
}

export function useFeeExpenditures(params: operations['getFeeExpenditures']['parameters']['query']) {
  return useQuery({
    queryKey: ['uccFeeExpenditures', params],
    queryFn: async () => {
      const result = await uccFilingAnalyticsClient.GET('/ucc-analytics/fee-expenditures', {
        params: { query: params },
      });
      return unwrapUccResponse(result);
    },
    enabled: !!params.tenantId,
  });
}

/**
 * The tenant's portfolio perfection summaries, newest first (the route orders
 * by summaryGeneratedAt descending), so `data[0]` is the latest summary.
 */
export function usePerfectionStatus(params: { tenantId: string }) {
  return useQuery({
    queryKey: ['uccPerfectionStatus', params],
    queryFn: async () => {
      const result = await uccFilingAnalyticsClient.GET('/ucc-analytics/perfection-status', {
        params: {
          query: { tenantId: params.tenantId, limit: 1 },
        },
      });
      return unwrapUccResponse(result);
    },
    enabled: !!params.tenantId,
  });
}
